
import React, { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Profile, Company } from '@/lib/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, UserPlus, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { InviteUserDialog } from './InviteUserDialog';
import { UsersTable } from './UsersTable';

interface UserManagementCardProps {
  companies: Company[];
}

export const UserManagementCard: React.FC<UserManagementCardProps> = ({ companies }) => {
  const { toast } = useToast();
  const [users, setUsers] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredUsers, setFilteredUsers] = useState<Profile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search query
    const filtered = users.filter(
      user =>
        (user.first_name && user.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.last_name && user.last_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.role && user.role.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setFilteredUsers(filtered);
  }, [users, searchQuery]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const typedUsers = (data || []).map(user => ({
        ...user,
        role: user.role as any
      }));

      setUsers(typedUsers);
      setFilteredUsers(typedUsers);
    } catch (error: any) {
      toast({
        title: "Failed to load users",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserUpdated = (updatedUser: Profile) => {
    setUsers(users.map(u => {
      if (u.id === updatedUser.id) {
        return updatedUser;
      }
      return u;
    }));
  };

  const handleUserDeleted = (userId: string) => {
    setUsers(users.filter(u => u.id !== userId));
  };

  const setDeletingUserIdState = (id: string | null) => {
    setDeletingUserId(id);
  };

  const handleResetPasswordOpen = (userEmail: string) => {
    // Populate search filter when dialog opens
    setSearchQuery(userEmail);
  };

  const handleResetPasswordSuccess = (userEmail: string) => {
    // Keep search field populated after successful reset
    setSearchQuery(userEmail);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              View and manage user accounts, assign roles and companies.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchUsers}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <InviteUserDialog 
              isOpen={isAddUserDialogOpen} 
              onOpenChange={setIsAddUserDialogOpen} 
            />
          </div>
        </div>
      </CardHeader>

      <div className="px-6">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search users by name, email, or role..."
            className="pl-9 pr-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <CardContent className="p-0 mt-4">
        <UsersTable 
          users={filteredUsers} 
          companies={companies} 
          isLoading={isLoading} 
          deletingUserId={deletingUserId}
          onUserUpdated={handleUserUpdated}
          onUserDeleted={handleUserDeleted}
          setDeletingUserId={setDeletingUserIdState}
          onResetPasswordOpen={handleResetPasswordOpen}
          onResetPasswordSuccess={handleResetPasswordSuccess}
        />
      </CardContent>
      <CardFooter className="py-4 border-t">
        <div className="text-sm text-muted-foreground">
          Total: {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
        </div>
      </CardFooter>
    </Card>
  );
};
